import { GaugeIcon } from "@phosphor-icons/react";
import type {
  ColDef,
  ICellRendererParams,
  SortChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import Buzzsumo from "@/assets/svg/Buzzsumo.svg?react";
import Moz from "@/assets/svg/Moz.svg?react";
import Readership from "@/assets/svg/Readership.svg?react";
import ArticleMentionText from "@/components/ui/article-card/article-mention-text";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import type { GetFeedArticlesTableDataVariables } from "@/data-access/discovery-view/queries/discovery-view-queries";
import type { FeedArticleProjectionOrdering } from "@/data-access/shared/types/feed-article-projection.types";
import { agGridTheme } from "@/lib/ag-grid-theme";
import { useFeature } from "@/lib/feature-management";
import { convertSortModelToOrdering } from "@/lib/utils/articleSortOrderingUtils";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import { useDiscoveryContext } from "@/store/discovery/discovery-params.context";
import FeedArticleCellRenderer from "./FeedArticleCellRenderer";
import { createUnifiedOverlay } from "./FeedArticlesTableStates";
import {
  createFeedArticlesDatasource,
  startNewDatasourceSession,
} from "./feedArticlesTableDatasource";

interface InnerHeaderWithIconProps {
  displayName?: string;
  icon?: React.ReactNode;
}

/**
 * Inner header component that adds an icon before the header text
 * This is used with AG Grid's default header component to preserve sorting functionality
 */
const InnerHeaderWithIcon = ({
  displayName,
  icon,
}: InnerHeaderWithIconProps) => {
  return (
    <div className="flex items-center gap-2">
      {icon && <span className="shrink-0">{icon}</span>}
      <span>{displayName}</span>
    </div>
  );
};

const FeedArticlesTable = () => {
  const gridRef = useRef<AgGridReact>(null);
  const hasInitializedSort = useRef(false);
  const isInitialMount = useRef(true);
  const enableDiscoveryTabExp = useFeature("enable-discovery-tab-exp", false);
  const isFetchingArticles = useDiscoveryContentManagementStore(
    (state) => state.isFetchingArticles,
  );
  const error = useDiscoveryContentManagementStore((state) => state.error);
  const totalCount = useDiscoveryContentManagementStore(
    (state) => state.totalCount,
  );
  const isInitialLoading = useDiscoveryContentManagementStore(
    (state) => state.isInitialLoading,
  );

  const { feedId, selectedFilters, startDate, endDate } = useDiscoveryContext();
  const searchTerm = useDiscoveryContentManagementStore(
    (state) => state.search.searchTerm,
  );

  // Local state for ordering - default to DATE descending
  const [ordering, setOrdering] = useState<FeedArticleProjectionOrdering>([
    { datePublished: "DESC_NULLS_LAST" },
  ]);

  const filters = useMemo(() => {
    const filterObj: GetFeedArticlesTableDataVariables["filters"] = {
      ...(feedId ? { feedId } : {}),
      ...(startDate && endDate ? { dateRange: { startDate, endDate } } : {}),
      ...(selectedFilters as GetFeedArticlesTableDataVariables["filters"]),
    };
    return filterObj;
  }, [feedId, selectedFilters, startDate, endDate]);

  const datasource = useMemo(() => {
    startNewDatasourceSession();
    return createFeedArticlesDatasource(() => filters, searchTerm, ordering);
  }, [filters, searchTerm, ordering]);

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.purgeInfiniteCache();
      gridRef.current.api.refreshInfiniteCache();
    }
  }, [datasource]);

  const handleRetry = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshInfiniteCache();
    }
  };

  const shouldShowOverlay =
    !isFetchingArticles && !error && !isInitialLoading && totalCount === 0;
  const shouldShowErrorOverlay = !isFetchingArticles && !!error;

  const UnifiedOverlay = createUnifiedOverlay(handleRetry);

  useEffect(() => {
    if (!gridRef.current?.api) return;

    if (shouldShowErrorOverlay || shouldShowOverlay) {
      gridRef.current.api.showNoRowsOverlay();
    } else {
      gridRef.current.api.hideOverlay();
    }

    // Clear placeholder rows when there's an error so skeletons don't show behind overlay
    if (shouldShowErrorOverlay) {
      gridRef.current.api.setRowCount(0, true);
    }
  }, [shouldShowOverlay, shouldShowErrorOverlay]);

  // Reset sorting to default when feedId changes (skip initial mount)
  useEffect(() => {
    // Skip on initial mount - ordering is already set to default
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Purge cache first to prevent fetching with old ordering
    if (gridRef.current?.api) {
      gridRef.current.api.purgeInfiniteCache();
    }

    // Reset ordering to default: DATE descending
    setOrdering([{ datePublished: "DESC_NULLS_LAST" }]);

    // Update AG Grid column state
    if (gridRef.current?.api) {
      gridRef.current.api.applyColumnState({
        state: [{ colId: "DATE", sort: "desc" }],
        defaultState: { sort: null },
      });
    }
  }, [feedId]);

  // Initialize AG Grid with default sort when grid is ready
  const handleGridReady = () => {
    if (!gridRef.current?.api || hasInitializedSort.current) return;

    // Set default sort: DATE descending
    gridRef.current.api.applyColumnState({
      state: [{ colId: "DATE", sort: "desc" }],
      defaultState: { sort: null },
    });
    hasInitializedSort.current = true;
  };

  // Handle sort changes from AG Grid
  const handleSortChanged = (event: SortChangedEvent) => {
    if (!event.api) return;

    // Get column states and filter for sorted columns
    const columnStates = event.api.getColumnState();
    const sortModel = columnStates
      .filter((col) => col.sort !== null && col.sort !== undefined)
      .map((col) => ({
        colId: col.colId ?? "",
        sort: col.sort ?? "asc",
      }));

    const newOrdering = convertSortModelToOrdering(sortModel);

    // Update local state with new ordering
    // The datasource will be recreated due to ordering dependency in useMemo
    // The useEffect on line 84-89 will handle refreshing when datasource changes
    setOrdering(newOrdering);
  };

  const columnDefs: ColDef[] = [
    {
      field: "DATE",
      headerName: "Date",
      width: 120,
      minWidth: 100,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
        />
      ),
    },
    {
      field: "PUBLICATION",
      headerName: "Publication",
      width: 150,
      minWidth: 120,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
        />
      ),
    },
    {
      field: "HEADLINE",
      headerName: "Headline",
      width: 300,
      minWidth: 200,
      resizable: true,
      wrapText: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
          wrapText={true}
          isLink={true}
          valueClassName="text-[#0039FF]"
          url={(data) => data?.URL}
        />
      ),
    },
    {
      field: "AUTHOR",
      headerName: "Author",
      width: 120,
      minWidth: 100,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
        />
      ),
    },
    {
      field: "IMPACT_SCORE",
      headerName: "LLM Impact Score",
      width: 160,
      minWidth: 160,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
          prefixRenderer={<GaugeIcon className="h-4 w-4 text-violet-800" />}
          valueClassName="text-violet-800"
          numberFormat="commas"
        />
      ),
    },
    {
      field: "SENTIMENT",
      headerName: "Sentiment",
      width: 160,
      minWidth: 160,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
          showSentimentBadge={true}
        />
      ),
    },
    {
      field: "PROMINENCE",
      headerName: "Prominence",
      width: 160,
      minWidth: 160,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
        />
      ),
    },
    {
      field: "TIER",
      headerName: "Tier",
      width: 120,
      minWidth: 100,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
        />
      ),
    },
    {
      field: "READERSHIP",
      headerName: "Readership",
      width: 180,
      minWidth: 180,
      resizable: true,
      headerComponentParams: {
        innerHeaderComponent: InnerHeaderWithIcon,
        innerHeaderComponentParams: {
          icon: <Readership height={14} />,
        },
      },
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
          numberFormat="abbreviate"
        />
      ),
    },
    {
      field: "DOMAIN_AUTHORITY",
      headerName: "Domain Authority",
      width: 230,
      minWidth: 220,
      resizable: true,
      headerComponentParams: {
        innerHeaderComponent: InnerHeaderWithIcon,
        innerHeaderComponentParams: {
          icon: <Moz height={24} width={26} />,
        },
      },
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
          numberFormat="commas"
        />
      ),
    },
    {
      field: "SOCIAL_ENGAGEMENTS",
      headerName: "Social Engagements",
      width: 220,
      minWidth: 200,
      resizable: true,
      headerComponentParams: {
        innerHeaderComponent: InnerHeaderWithIcon,
        innerHeaderComponentParams: {
          icon: <Buzzsumo height={14} />,
        },
      },
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
          numberFormat="abbreviate"
          showSocialEngagementTooltip={true}
        />
      ),
    },
    {
      field: "CATEGORY",
      headerName: "Category",
      width: 160,
      minWidth: 160,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
        />
      ),
    },
    {
      field: "COUNTRY",
      headerName: "Country",
      width: 130,
      minWidth: 110,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
        />
      ),
    },
    {
      field: "REGION",
      headerName: "Region",
      width: 120,
      minWidth: 100,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
        />
      ),
    },
    {
      field: "LANGUAGE",
      headerName: "Language",
      width: 160,
      minWidth: 160,
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
        />
      ),
    },
    {
      field: "NARRATIVE_CLUSTERS",
      headerName: "Narrative Cluster",
      width: 300,
      minWidth: 200,
      resizable: true,
      wrapText: true,
      cellRenderer: (params: ICellRendererParams) => (
        <FeedArticleCellRenderer
          isFetchingArticles={isFetchingArticles}
          params={params}
          wrapText={true}
        />
      ),
    },
    {
      colId: "FIRST_MENTION",
      field: "FIRST_MENTION",
      headerName: "First Mention",
      width: 400,
      minWidth: 250,
      resizable: true,
      sortable: false,
      cellStyle: {
        display: "flex",
        alignItems: "flex-start",
        whiteSpace: "normal",
        lineHeight: "1.4",
        padding: "8px 4px",
      },
      cellRenderer: (params: ICellRendererParams) => {
        if (isFetchingArticles && !params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        const snippet = params.value as string | null;
        if (!snippet) return "-";
        const term =
          (params.data as { FIRST_MENTION_TERM?: string | null })
            ?.FIRST_MENTION_TERM ?? "";
        return (
          <ArticleMentionText
            snippet={snippet}
            term={term}
            className="text-inherit"
          />
        );
      },
    },
  ];

  return (
    <>
      {/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: TODO – replace <div> with <section> in a dedicated a11y PR */}
      <div
        className="w-full h-full relative"
        aria-busy={isFetchingArticles}
        aria-live="polite"
        aria-label={isFetchingArticles ? "Loading articles" : undefined}
        style={
          {
            "--ag-grid-min-height": "100px",
          } as React.CSSProperties
        }
      >
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: true,
            resizable: true,
          }}
          rowModelType="infinite"
          datasource={datasource}
          cacheBlockSize={50}
          maxBlocksInCache={10}
          infiniteInitialRowCount={5}
          rowHeight={80}
          theme={agGridTheme}
          enableCellTextSelection
          suppressDragLeaveHidesColumns={true}
          rowSelection={
            enableDiscoveryTabExp
              ? {
                  mode: "multiRow",
                  checkboxes: true,
                }
              : undefined
          }
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[10, 20, 50, 100]}
          loadingOverlayComponent={() => null}
          noRowsOverlayComponent={UnifiedOverlay}
          onGridReady={handleGridReady}
          onSortChanged={handleSortChanged}
        />
      </div>
    </>
  );
};

export default FeedArticlesTable;
